import { Button } from "@/common/components/button";
import { FINANCIAL_REPORT_URL } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useState } from "react";
import styled from "styled-components";

export interface IRequestDetailsProps {
  id: string;
  submitted_by: string;
  amount_requested: string;
  status: string;
  justification: string;
  submittion_date: string;
}

const statusColors: Record<string, string> = {
  accepted: "success",
  pending: "mild_danger",
  rejected: "danger",
};

const RequetDetailsWrapper = styled.div<{ statusColor?: string }>`
  width: 100%;
  max-width: 60rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.bg.prim_500};
  border-radius: 0.5rem;
  color: ${({ theme }) => theme.text.prim_100};
  padding: 2rem 4rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
  }

  p {
    font-size: 1rem;
    color: ${({ theme }) => theme.text.prim_200};
  }

  .details {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
    margin-bottom: 1rem;

    .detail {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      h3 {
        font-size: 1rem;
        font-weight: 500;
        color: ${({ theme }) => theme.text.prim_100};
      }

      span {
        font-weight: 600;
        color: ${({ theme }) => theme.text.prim_200};
      }
    }

    @media (max-width: ${({ theme }) => theme.screen.mobile}) {
      grid-template-columns: 1fr;
    }

    @media (min-width: ${({ theme }) => theme.screen.tablet}) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${({ theme }) => theme.screen.smallScreen}) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .status {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;

    h3 {
      font-size: 1rem;
      font-weight: 500;
      color: ${({ theme }) => theme.text.prim_100};
    }

    span {
      font-weight: 600;
      color: ${({ theme, statusColor }) =>
        theme.text[statusColor as string] || theme.text.prim_200};
      border: 1px solid
        ${({ theme, statusColor }) =>
          theme.text[statusColor as string] || theme.text.prim_200};
      width: fit-content;
      padding: 0.5rem 1rem;
      border-radius: 2rem;
    }
  }

  .justification {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 100%;
    margin-top: 1rem;

    h3 {
      font-size: 1rem;
      font-weight: 500;
      color: "black";
    }

    p {
      font-weight: 400;
      color: ${({ theme }) => theme.text.prim_200};
    }
  }

  .preview-control {
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;

    p {
      color: ${({ theme }) => theme.text.danger};
      font-size: 1rem;
      font-weight: 500;
      margin-top: 0.5rem;
    }
  }
`;

const RequetDetails = ({
  data,
  close,
}: {
  data: IRequestDetailsProps;
  close: () => void;
}) => {
  const makeRequest = useApiCallHandling();
  const statusColor =
    statusColors[data?.status?.toLocaleLowerCase()] || "muted";
  const [loading, setLaoding] = useState<boolean>(false);
  const [activeSelection, setActiveSelection] = useState<string | null>(null);

  const handleSubmission = (approveType: string) => {
    setLaoding(true);
    setActiveSelection(approveType);
    const route =
      approveType === "approve" ? "approve-request" : "decline-request";
    makeRequest(
      FINANCIAL_REPORT_URL + `/${route}/${data?.id}`,
      "POST",
      {
        id: data?.id,
      },
      () => {
        close();
      },
      () => {},
      () => {
        setLaoding(false);
      }
    );
  };

  const canBeApproved =
    data?.status?.toLocaleLowerCase() === "pending_hospital_approval";

  const cannotBeApproveMessage = "This request has already been approve";
  return (
    <RequetDetailsWrapper statusColor={statusColor}>
      <h1>Company Data</h1>
      <div className="details">
        <div className="detail">
          <h3>Submitted Date</h3>
          <span> {new Date(data.submittion_date).toLocaleDateString()} </span>
        </div>
        <div className="detail">
          <h3>Submitted By</h3>
          <span> {data.submitted_by} </span>
        </div>
        <div className="detail">
          <h3>Amount Requested</h3>
          <span> {data.amount_requested} </span>
        </div>
      </div>
      <div className="status">
        <h3>Status:</h3>
        <span>{data.status}</span>
      </div>
      <div className="justification">
        <h3>Justification:</h3>
        <p>{data.justification}</p>
      </div>

      {canBeApproved ? (
        <div className="preview-control">
          <Button
            loading={activeSelection === "approve" && loading}
            onClick={() => handleSubmission("approve")}
            primary
          >
            Approve
          </Button>
          <Button
            loading={activeSelection === "decline" && loading}
            onClick={() => handleSubmission("decline")}
            bg="red"
          >
            Decline
          </Button>
        </div>
      ) : (
        <div className="preview-control">
          <p>{cannotBeApproveMessage}</p>
        </div>
      )}
    </RequetDetailsWrapper>
  );
};

export default RequetDetails;
